import {Button, view, TextField} from '@sail/ui';
import {LightCard} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {useCallback, useEffect, useState, useRef} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

const messages = defineMessages({
  formTitle: {
    id: 'mse_inline_data.alo_auth.email_input_form.formTitle',
    description: 'The title of the email input form',
    defaultMessage: 'Enter email address',
  },
  fieldPlaceholderEmail: {
    id: 'mse_inline_data.alo_auth.email_input_form.fieldPlaceholderEmail',
    description: 'The placeholder for the email input field',
    defaultMessage: 'Email',
  },
  submitText: {
    id: 'mse_inline_data.alo_auth.email_input_form.submitText',
    description: 'Text on the submit button',
    defaultMessage: 'Submit',
  },
});

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const validateEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  return trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
};

export type ValueProps = {
  email: string;
  errorMessage?: string;
};

export const EmailInputForm = (props: InlineDatumReactComponentProps) => {
  const {onDone, isDone, datum} = props;

  const value = datum.value as ValueProps;
  const {email: initialEmail, errorMessage} = value;

  const intl = useIntl();
  const analytics = useAnalytics();

  const [email, setEmail] = useState(initialEmail || '');
  const [previousEmail, setPreviousEmail] = useState(initialEmail || '');
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [isValid, setIsValid] = useState(false);
  const [userModified, setUserModified] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsEditing(!isDone);
  }, [isDone]);

  useEffect(() => {
    if (initialEmail) {
      const isInitialValid = validateEmail(initialEmail);
      setIsValid(isInitialValid);
      setPreviousEmail(initialEmail);
    }
  }, [initialEmail]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmail = e.target.value;
      setEmail(newEmail);

      const isEmailValid = validateEmail(newEmail);
      setIsValid(isEmailValid);

      if (newEmail !== previousEmail) {
        setUserModified(true);
      } else {
        setUserModified(false);
      }
    },
    [previousEmail],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedEmail = email.trim();

      if (!isValid || !validateEmail(trimmedEmail)) {
        return;
      }

      setIsEditing(false);
      setPreviousEmail(trimmedEmail);

      onDone({
        type: 'success',
        message: 'Email Input Form Data',
        extra: {
          dataWithPii: {
            email: trimmedEmail,
          },
        },
      });

      analytics.action('email_input_form_submit_click', {
        email_length: trimmedEmail.length,
        email_domain: trimmedEmail.split('@')[1] || '',
      });
    },
    [email, isValid, onDone, analytics],
  );

  const getError = () => {
    if (!userModified && errorMessage) {
      return errorMessage;
    }

    return undefined;
  };

  if (!isEditing) return null;

  return (
    <view.div css={{marginTop: 'small', width: 'fill'}}>
      <LightCard>
        <view.form css={{gap: 'medium', stack: 'y'}} onSubmit={handleSubmit}>
          <view.div css={{width: 'fill'}}>
            <TextField
              id="email-field"
              ref={inputRef}
              type="email"
              label={intl.formatMessage(messages.formTitle)}
              placeholder={intl.formatMessage(messages.fieldPlaceholderEmail)}
              width="maximized"
              value={email}
              onChange={handleEmailChange}
              autoComplete="email"
              data-testid="email-input"
              error={getError()}
              subviews={{
                label: {
                  css: {
                    marginBottom: 'medium',
                    font: 'heading.small',
                  },
                },
              }}
            />
          </view.div>

          <Button
            css={{width: 'fill'}}
            data-testid="email-submit-btn"
            type="primary"
            behavior="submit"
            disabled={!isValid}
          >
            <FormattedMessage {...messages.submitText} />
          </Button>
        </view.form>
      </LightCard>
    </view.div>
  );
};
